import { KitStatuses, } from '@repo/types';
import { parseDate } from './parseDate';
/**
 * Source of truth for status → title mapping.
 *
 * Drives both {@link kitStatusParser} (status → title) and the no-rule branch
 * of {@link getKitStatusQuery} (title → statuses). Add a status here once and
 * both directions stay in sync.
 *
 * `Processing` is the fallback for any status not enumerated below.
 */
const STATUSES_BY_PARSED_TITLE = {
    'Results ready': [
        KitStatuses.EXPERT_REVIEW_COMPLETE,
        KitStatuses.RESULTS_READY,
    ],
    Activated: [KitStatuses.ACTIVATED],
    'Not yet received': [
        KitStatuses.CONFIRM_KIT_RETURNED_TO_LAB_BY_CUSTOMER,
        KitStatuses.DELIVERED_TO_CUSTOMER,
        KitStatuses.INITIALIZED,
        KitStatuses.IN_TRANSIT_TO_CUSTOMER,
        KitStatuses.IN_TRANSIT_TO_LAB,
        KitStatuses.LABEL_EXPIRY_EMAIL_SENT,
    ],
    Void: [
        KitStatuses.VOID,
        KitStatuses.VOID_CUSTOMER_REFUND,
        KitStatuses.VOID_INTERNAL,
        KitStatuses.VOID_LAB_ERROR,
        KitStatuses.VOID_LOST_IN_MAIL,
        KitStatuses.VOID_RETURN_LABEL_EXPIRED,
        KitStatuses.VOID_SAMPLE_EXPIRED,
        KitStatuses.VOID_SHIPPO_SHIPMENT_FAILED,
    ],
    Processing: [
        KitStatuses.DELIVERED_TO_LAB,
        KitStatuses.SEQUENCES_DELIVERED,
        KitStatuses.PIPELINE_COMPLETE,
        KitStatuses.RESULTS_READY_PRACTITIONER,
        KitStatuses.RETURN_MISSING,
    ],
};
/** O(1) reverse index of {@link STATUSES_BY_PARSED_TITLE}. */
const PARSED_TITLE_BY_STATUS = new Map(Object.entries(STATUSES_BY_PARSED_TITLE).flatMap(([title, statuses]) => statuses.map(s => [s, title])));
export function kitStatusParser(status) {
    if (!status)
        return 'Processing';
    return PARSED_TITLE_BY_STATUS.get(status) ?? 'Processing';
}
/**
 * Source of truth for kit-status resolution. Evaluated top-to-bottom;
 * {@link parseKitStatus} returns the first matching rule, so order
 * encodes priority (e.g. `Void` wins over `Ready`).
 */
const KIT_STATUS_RULES = [
    {
        title: 'Void',
        color: 'red',
        nonNullFields: ['date_voided'],
    },
    { title: 'Ready', color: 'green', nonNullFields: ['date_results_ready'] },
    {
        title: 'Practitioner review',
        color: 'orange',
        nonNullFields: ['date_results_ready_practitioner'],
    },
    { title: 'Lab', color: 'blue', nonNullFields: ['date_delivered_to_lab'] },
    {
        title: 'Shipped to lab',
        color: 'orange',
        nonNullFields: ['date_in_transit_to_lab'],
    },
    { title: 'Activated', color: 'purple', nonNullFields: ['date_activated'] },
    {
        title: 'Delivered',
        color: 'purple',
        nonNullFields: ['date_delivered_to_customer'],
    },
    {
        title: 'In transit',
        color: 'orange',
        nonNullFields: ['date_in_transit_to_customer'],
    },
    { title: 'Ordered', color: 'purple', nonNullFields: ['date_purchased'] },
];
/** Evaluates a single rule against a kit's DB fields. See {@link KitStatusRule}. */
function ruleMatches(rule, data) {
    const nonNullMatches = rule.nonNullFields
        ? rule.nonNullFields.every(f => data[f] != null)
        : true;
    const nullMatches = rule.nullFields
        ? rule.nullFields.every(f => data[f] == null)
        : true;
    return nonNullMatches && nullMatches;
}
/**
 * Maps a kit's DB row (the `status` enum + `date_*` columns) to the
 * frontend-facing title and color shown in the UI.
 *
 * Walks {@link KIT_STATUS_RULES} in order and returns the first match.
 * Falls back to the human-readable form of the raw `status` enum when
 * no rule matches (e.g. `initialized` → `'Not yet received'`).
 */
export function parseKitStatus(data) {
    for (const rule of KIT_STATUS_RULES) {
        if (ruleMatches(rule, data)) {
            return { title: rule.title, color: rule.color };
        }
    }
    return {
        title: kitStatusParser(data.status),
        color: 'yellow',
    };
}
export function resolveKitStatusTitle(kit) {
    const title = parseKitStatus(kit).title;
    if (title === 'Ready' && kit.results_state === 'partial') {
        return 'Ready - Partial';
    }
    return title;
}
/**
 * Colour for a resolved title.
 *
 * `'Ready - Partial'` is not in {@link KIT_STATUS_RULES} — it is layered on top
 * of `'Ready'` — so it has no colour of its own and deliberately reuses Ready's,
 * keeping a released kit visually released whether or not an add-on is still
 * outstanding.
 */
export function resolveKitStatusColor(kit) {
    return parseKitStatus(kit).color;
}
/**
 * When a kit's last outstanding component landed, for a kit that is now
 * complete but was released partial earlier.
 *
 * Derived from the variant rows rather than a backend field: a variant whose
 * results arrived *after* the kit's own release date can only have been
 * outstanding at release time. Returns `undefined` for a kit that was complete
 * when released (no variant postdates it), and for one whose variants were not
 * eager-loaded — in both cases the Details cell shows its ordinary released
 * date rather than claiming an update that may not have happened.
 */
export function resolveResultsUpdatedDate(kit) {
    if (!kit.date_results_ready || !kit.variants?.length)
        return undefined;
    const releasedAt = parseDate(kit.date_results_ready).getTime();
    let latest;
    let latestAt = releasedAt;
    for (const variant of kit.variants) {
        if (!variant.date_results_ready)
            return undefined; // still partial
        const readyAt = parseDate(variant.date_results_ready).getTime();
        if (readyAt > latestAt) {
            latest = variant.date_results_ready;
            latestAt = readyAt;
        }
    }
    return latest;
}
/**
 * Inverse of {@link parseKitStatus}: given a frontend title, returns the
 * DB predicate that selects kits displaying that title.
 *
 * - If the title corresponds to a date-driven rule in {@link KIT_STATUS_RULES},
 *   returns the rule's null/non-null field constraints.
 * - Otherwise, the title comes from the {@link kitStatusParser} fallback path,
 *   so we filter by DB `status` via {@link STATUSES_BY_PARSED_TITLE}.
 */
export function getKitStatusQuery(title) {
    const ruleIndex = KIT_STATUS_RULES.findIndex(r => r.title === title);
    if (ruleIndex !== -1) {
        const rule = KIT_STATUS_RULES[ruleIndex];
        // Earlier rules short-circuit in parseKitStatus, so to match this title
        // their trigger fields must all be null. Combine those with the rule's
        // own explicit nullFields.
        const precedingNonNullFields = KIT_STATUS_RULES.slice(0, ruleIndex).flatMap(r => r.nonNullFields ?? []);
        return {
            null_forbidden_fields: rule.nonNullFields,
            null_required_fields: [
                ...precedingNonNullFields,
                ...(rule.nullFields ?? []),
            ],
        };
    }
    const precedingNonNullFields = KIT_STATUS_RULES.flatMap(r => r.nonNullFields ?? []);
    return {
        status: STATUSES_BY_PARSED_TITLE[title] ?? [],
        null_required_fields: precedingNonNullFields,
    };
}
/**
 * Alerts that take over a kit's Details cell, in precedence order.
 *
 * Also the option list for the Details column's "Filter by details" control.
 */
export const KIT_DETAIL_ALERTS = [
    'Need to activate kit',
    'Survey incomplete',
];
/** Every title {@link parseKitStatus} can return. */
export const KIT_STATUS_TITLES = [
    ...new Set([
        ...KIT_STATUS_RULES.map(r => r.title),
        ...Object.keys(STATUSES_BY_PARSED_TITLE),
    ]),
];
/**
 * Statuses whose Details cell shows the kit's shipping details.
 *
 * These are the stages before the customer activates the kit, where the
 * recipient is the only thing a provider can act on — they cannot yet know
 * whose kit it is, nor can surveys have been completed.
 */
export const SHIPPING_DETAILS_KIT_STATUS_TITLES = [
    'Ordered',
    'In transit',
    'Delivered',
];
/**
 * Whether a kit's Details cell shows shipping details.
 *
 * Takes the title as {@link ParsedKitStatus} exposes it (a plain `string`)
 * so callers do not each have to narrow it.
 */
export function showsShippingDetails(title) {
    return SHIPPING_DETAILS_KIT_STATUS_TITLES.includes(title);
}
/**
 * Statuses whose Details cell never shows an alert, because it is showing
 * something else entirely (a replacement kit, or shipping details).
 *
 * The shipping-details titles are redundant for the survey warning — the rule
 * ordering above puts `Activated` ahead of them, so a kit holding one of those
 * titles provably has a null `date_activated`. They stay listed so the
 * DB-side filter does not emit branches that can never match.
 */
export const ALERTLESS_KIT_STATUS_TITLES = [
    'Void',
    ...SHIPPING_DETAILS_KIT_STATUS_TITLES,
];
/** Statuses a kit can reach without ever having been activated. */
export const NEEDS_ACTIVATION_KIT_STATUS_TITLES = [
    'Shipped to lab',
    'Lab',
];
/**
 * Whether a kit is outstanding on its surveys in a way worth flagging.
 *
 * Deliberately a property of the kit's dates rather than of its status: the
 * warning is only actionable once a patient holds the kit and has activated
 * it. Before that there is frequently no known patient to chase, and a kit
 * can reach a surprising status — or fall back to a `status`-derived title
 * that no allowlist covers — without ever having been activated.
 */
export function hasIncompleteSurvey(data) {
    return data.date_activated != null && data.date_survey_completed == null;
}
/**
 * Which alert, if any, a kit's Details cell shows.
 *
 * Mirrors the branch order of the portals' `ReleaseCell` so that filtering on
 * an alert returns exactly the rows displaying it. A kit that shipped without
 * being activated can't have completed its surveys either, so needing
 * activation outranks the survey warning.
 */
export function parseKitDetailAlert(data) {
    const title = parseKitStatus(data).title;
    if (ALERTLESS_KIT_STATUS_TITLES.includes(title))
        return null;
    if (NEEDS_ACTIVATION_KIT_STATUS_TITLES.includes(title) &&
        data.date_activated == null) {
        return 'Need to activate kit';
    }
    if (hasIncompleteSurvey(data))
        return 'Survey incomplete';
    return null;
}
const sampleDateComparator = (a, b) => {
    if (!a.sample_date && b.sample_date) {
        return 1;
    }
    else if (a.sample_date && !b.sample_date) {
        return -1;
    }
    else if (a.sample_date && b.sample_date) {
        return (new Date(b.sample_date).getTime() - new Date(a.sample_date).getTime());
    }
    else {
        return 0;
    }
};
export function kitResultsViewable(kit, impersonating = false) {
    if (!kit)
        return false;
    const status = kit.status;
    const date_results_ready = kit.date_results_ready;
    if (date_results_ready)
        return true;
    if (!status)
        return false;
    if (impersonating) {
        return [
            KitStatuses.RESULTS_READY_PRACTITIONER,
            KitStatuses.RESULTS_READY,
            KitStatuses.EXPERT_REVIEW_COMPLETE,
        ].includes(status);
    }
    return [
        KitStatuses.RESULTS_READY,
        KitStatuses.EXPERT_REVIEW_COMPLETE,
    ].includes(status);
}
export const sortKitsByResultsViewableOrLatest = (kits) => {
    if (!kits?.length)
        return [];
    return kits
        .filter(kit => kitStatusParser(kit.status) !== 'Void')
        .sort((a, b) => {
        // first sort by status "results_ready"
        if (kitResultsViewable(a) && !kitResultsViewable(b)) {
            return -1;
        }
        else if (!kitResultsViewable(a) && kitResultsViewable(b)) {
            return 1;
        }
        else {
            return sampleDateComparator(a, b);
        }
    });
};
export function sortKitsByLatestSampleDate(kits) {
    if (!kits?.length)
        return [];
    if (kits.length === 1)
        return kits;
    return kits.sort(sampleDateComparator) || [];
}
export function kitStatusColorParser(status) {
    if (!status)
        return 'default';
    switch (status) {
        case KitStatuses.EXPERT_REVIEW_COMPLETE:
        case KitStatuses.RESULTS_READY:
            return 'good';
        case KitStatuses.DELIVERED_TO_LAB:
        case KitStatuses.PIPELINE_COMPLETE:
        case KitStatuses.SEQUENCES_DELIVERED:
            return 'ok';
        case KitStatuses.VOID_CUSTOMER_REFUND:
        case KitStatuses.VOID_INTERNAL:
        case KitStatuses.VOID_LAB_ERROR:
        case KitStatuses.VOID_LOST_IN_MAIL:
        case KitStatuses.VOID_RETURN_LABEL_EXPIRED:
        case KitStatuses.VOID_SAMPLE_EXPIRED:
        case KitStatuses.VOID_SHIPPO_SHIPMENT_FAILED:
        case KitStatuses.RETURN_MISSING:
            return 'bad';
        case KitStatuses.ACTIVATED:
        case KitStatuses.CONFIRM_KIT_RETURNED_TO_LAB_BY_CUSTOMER:
        case KitStatuses.DELIVERED_TO_CUSTOMER:
        case KitStatuses.INITIALIZED:
        case KitStatuses.IN_TRANSIT_TO_CUSTOMER:
        case KitStatuses.IN_TRANSIT_TO_LAB:
        case KitStatuses.LABEL_EXPIRY_EMAIL_SENT:
        default:
            return 'default';
    }
}
/**
 * Practitioners and partners may only retarget the activation reminders while
 * the kit is unclaimed; once activated it belongs to a tinyaccount (ENG-4867).
 */
export function isBuyerEmailEditable(kit) {
    return !kit.date_activated && !kit.date_voided && kit.status !== 'void';
}
/**
 * Email the kits tables show. An editable cell prefers `buyer_email` — the
 * address it edits and reminders target — so a save is visible; without that it
 * shows the caller's fallback (mainaccount email, fulfillment contact, ...)
 * rather than blanking, since `buyer_email` is nullable.
 *
 * `isEditable` is passed in rather than derived so the feature flag and the
 * kit-state gate stay in one place at the call site.
 */
export function resolveKitEmail(kit, fallback, isEditable) {
    if (isEditable)
        return kit.buyer_email || fallback;
    return fallback || kit.buyer_email;
}
//# sourceMappingURL=kitStatus.js.map